// frontend/src/pages/DataUploadPage.tsx
// v10.3.0 - DARK PREMIUM REFACTOR (FASE 1: ESTILOS & ESTRUCTURA)

import { useState, useEffect, ChangeEvent, FormEvent, FC } from 'react';
import { useMutation, useQueryClient, useQuery, useQueries } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import type { AxiosError, AxiosResponse } from 'axios';

import {
    Alert, AlertTitle, Box, Button,
    Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
    LinearProgress, MenuItem, Paper, Radio, RadioGroup, Select, SelectChangeEvent, styled,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Stack, Chip, Tooltip
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { currentOrigin, withReturnTo } from '../modules/pricingJourney/navigation';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';

import axiosInstance from '../config/axiosInstance';
import { FileType, ProcessingStatus } from '../types';
import { SYSTEM_FIELDS, SystemField } from '../config/systemFields';
import * as XLSX from 'xlsx';
import type { FileNormalizationAnalyzeResult } from '../types/fileNormalization';
import {
    analyzePricingFileNormalization,
    reviewPricingFileNormalization,
} from '../services/fileNormalizationService';
import OperationsDetailHeader from '../components/Operations/OperationsDetailHeader';
import { pageFrameSx } from '../modules/pricingJourney/components/PageHeader';
import { SkeletonBlock } from '../modules/pricingJourney/components/Skeletons';
import { DiscardSummary, detectFileType, type DiscardSummaryData } from '../modules/pricingJourney/components/UploadHelpers';
import EnginePanel from '../modules/pricingJourney/components/EnginePanel';
import { JourneyButton, JourneyChip } from '../modules/pricingJourney/components/JourneyBasics';
import { Notice } from '../modules/pricingJourney/components/JourneyBlocks';
import { J } from '../modules/pricingJourney/journeyTokens';
import { PLAN_LIMITS } from '../modules/pricingJourney/pricingPlans';
import { toSpanishNumber } from '../modules/pricingJourney/formatNumber';

// --- ESTILOS DARK PREMIUM (CONSTANTES) ---
const glassCardSx = {
    bgcolor: '#1e293b', 
    border: '1px solid #334155',
    borderRadius: 2,
    p: 3,
    height: '100%',
    width: '100%', // <--- ESTA LÍNEA ES LA CLAVE PARA EL 50/50
    display: 'flex', 
    flexDirection: 'column',
    boxShadow: 'none'
};

const uploadZoneSx = {
    border: '1px dashed #334155',
    borderRadius: 2,
    p: 4,
    textAlign: 'center',
    cursor: 'pointer',
    bgcolor: 'rgba(255, 255, 255, 0.02)',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    '&:hover': {
        bgcolor: 'rgba(56, 189, 248, 0.05)', // Tinte Cyan sutil
        borderColor: '#38bdf8'
    }
};

const stealthSelectSx = {
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-select': { 
        bgcolor: 'rgba(255, 255, 255, 0.03)', 
        border: '1px solid #334155',
        borderRadius: 1,
        py: 1,
        fontSize: '0.9rem',
        '&:focus': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
    },
    '&:hover .MuiSelect-select': { borderColor: '#64748b' },
    '& .MuiSelect-icon': { color: '#64748b' }
};

// --- NUEVOS TIPOS PARA EL PERFIL DE DATOS ---
interface ColumnProfileStats {
    dtype: string;
    total_count: number;
    non_null_count: number;
    null_count: number;
    null_percentage: number;
    unique_count?: number;
    min_date?: string;
    max_date?: string;
    min?: number;
    max?: number;
    mean?: number;
}

interface DataProfileResponse {
    file_type: string;
    row_count: number;
    column_count: number;
    column_profiles: Record<string, ColumnProfileStats>;
}

// --- Tipos y Constantes ---
type CostStrategy = 'direct' | 'historical';
type UploadableFileType = FileType | 'historical_costs';

interface UploadSectionState {
    file: File | null; uploading: boolean; error: string | null; successMessage: string | null;
    userHeaders: string[]; mapping: Record<string, string>; isMappedAndProcessed: boolean;
    gcsObjectName: string | null; currentFileType: UploadableFileType | null; showOverride: boolean;
    uploadMode: 'replace' | 'incremental' | 'replace_by_date_range'; 
    processingResult: DataProfileResponse | null;
    previewData: Record<string, string>;
    normalization: FileNormalizationAnalyzeResult | null;
    normalizationConfirmed: boolean;
    normalizationSheetName: string | null;
    normalizationHeaderRowIndex: number;
    normalizationReviewOpen: boolean;
    normalizationHeaderRowInput: string;
    // PRC-040: nombre del archivo subido (file se vacía tras subirlo).
    fileName?: string | null;
}

const initialSectionState: UploadSectionState = {
    file: null, uploading: false, error: null, successMessage: null, userHeaders: [],
    mapping: {}, isMappedAndProcessed: false, gcsObjectName: null, currentFileType: null, showOverride: false,
    uploadMode: 'replace',
    processingResult: null,
    previewData: {},
    normalization: null,
    normalizationConfirmed: false,
    normalizationSheetName: null,
    normalizationHeaderRowIndex: 0,
    normalizationReviewOpen: false,
    normalizationHeaderRowInput: '1',
};

interface ProcessPayload {
    file_type: UploadableFileType; mapping: Record<string, string>; gcs_object_name: string; cost_strategy?: CostStrategy;
}

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', height: 1, overflow: 'hidden',
    position: 'absolute', bottom: 0, left: 0, whiteSpace: 'nowrap', width: 1,
});

const formatDateTime = (isoString?: string): string => {
    if (!isoString) return 'N/A';
    try {
        const date = new Date(isoString);
        return date.toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) { return 'Fecha inválida'; }
};

const formatDateOnly = (isoString?: string | null): string => {
    if (!isoString) return 'N/A';

    const [datePart] = isoString.split('T');
    const [year, month, day] = datePart.split('-');

    return year && month && day
        ? `${day}/${month}/${year}`
        : datePart;
};

// --- COMPONENTE HIJO ESTABLE: UploadSection ---
interface UploadSectionProps {
    fileType: UploadableFileType;
    title: string;
    subtitle: string;
    parentState: UploadSectionState;
    updateParentState: (newState: Partial<UploadSectionState>) => void;
    processMutation: any;
    queryEnabled?: boolean;
    fieldsToMap: SystemField[];
    modalOnly?: boolean;
    // PRC-040: archivo reconocido por sus columnas en la zona común de arrastre.
    incomingFile?: File | null;
    onIncomingFileTaken?: () => void;
}

// *** INICIO DE LA MODIFICACIÓN ATÓMICA (DARK PREMIUM) ***
import { getDataStatus, getSavedMapping, getTransactionCount, getTransactionDateRange } from '../services/dataService'; 

const UploadSection: FC<UploadSectionProps> = ({ fileType, title, parentState, updateParentState, processMutation, queryEnabled, fieldsToMap, modalOnly = false, incomingFile, onIncomingFileTaken }) => {
    
    // 1. QUERY DE ESTADO (MANTENIDA)
    const { data: currentStatusDetail, isLoading: isLoadingStatus } = useQuery<ProcessingStatus | null, AxiosError>({
        queryKey: ['dataStatus', fileType],
        queryFn: () => getDataStatus(fileType as FileType),
        enabled: queryEnabled ?? true,
        staleTime: 5 * 60 * 1000,
        retry: (failureCount, err: AxiosError) => err.response?.status !== 404 && failureCount < 2,
        refetchOnWindowFocus: false,
    });

    // 2. QUERY DE TRANSACCIONES TOTALES (MANTENIDA)
    const { data: totalTransactionsData, isLoading: isLoadingTotalCount } = useQuery<{ total_count: number }, AxiosError>({
        queryKey: ['transactionCount'],
        queryFn: getTransactionCount,
        enabled: (queryEnabled ?? true) && fileType === 'transactions',
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    // 3. QUERY DE COBERTURA TEMPORAL DE TRANSACCIONES
    const { data: transactionDateRangeData } = useQuery({
        queryKey: ['transactionDateRange'],
        queryFn: getTransactionDateRange,
        enabled: (queryEnabled ?? true) && fileType === 'transactions',
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    // 4. QUERY DE MAPEO GUARDADO (MANTENIDA)
    const { data: savedMapping, isInitialLoading: isLoadingMapping } = useQuery<Record<string, string>, AxiosError>({
        queryKey: ['savedMapping', fileType],
        queryFn: () => getSavedMapping(fileType as FileType),
        enabled: queryEnabled ?? true,
    });

    const navigate = useNavigate();
    const location = useLocation();
    
    const [isAutoMapped, setIsAutoMapped] = useState(false);
    const [isReviewingMapping, setIsReviewingMapping] = useState(false);
    const [mappingMismatch, setMappingMismatch] = useState<string | null>(null);
    const [showDataDetails, setShowDataDetails] = useState(false);

    // --- NUEVO: LÓGICA DE INERCIA DE ESQUEMA ---
    const [isExpandedMode, setIsExpandedMode] = useState(false);

    // Detección de "Cold Start": ¿Es la primera vez o ya hay historia?
    // Consideramos que hay historia si savedMapping existe y tiene claves.
    const hasHistory = savedMapping && Object.keys(savedMapping).length > 0;

    // 1. Calculamos qué campos deben ser visibles
    const mandatoryKeys = fieldsToMap.filter(f => f.required).map(f => f.key);
    const savedKeys = savedMapping ? Object.keys(savedMapping) : [];
    const activeKeys = new Set([...mandatoryKeys, ...savedKeys]);

    // REGLA MAESTRA DE VISIBILIDAD:
    // - Si el usuario expandió manualmente -> TODO.
    // - Si NO hay historia (Carga inicial) -> TODO (Para facilitar el setup).
    // - Si HAY historia -> Solo lo conocido (Protección contra deriva).
    const visibleFields = (isExpandedMode || !hasHistory)
        ? fieldsToMap 
        : fieldsToMap.filter(f => activeKeys.has(f.key));

    // 2. Efecto de seguridad: Si expandes la estructura, forzamos REEMPLAZO TOTAL
    // FIX: Ahora cubre tanto 'incremental' como 'replace_by_date_range' (Transacciones)
    useEffect(() => {
        const isPartialMode = parentState.uploadMode === 'incremental' || parentState.uploadMode === 'replace_by_date_range';
        
        if (isExpandedMode && isPartialMode) {
            updateParentState({ uploadMode: 'replace' });
        }
    }, [isExpandedMode, parentState.uploadMode, updateParentState]);

    const handleToggleExpand = () => {
        setIsExpandedMode(true);
        updateParentState({ uploadMode: 'replace' }); // Forzar inmediatamente al abrir
    };

    const uploadConfirmedFile = async (
        file: File,
        sheetName: string | null,
        headerRowIndex: number | null,
    ) => {
        updateParentState({ uploading: true, error: null, successMessage: null });

        const formData = new FormData();
        formData.append('file', file);

        let uploadUrl = `/data/upload?file_type=${fileType}`;
        if (sheetName) {
            uploadUrl += `&sheet_name=${encodeURIComponent(sheetName)}`;
        }
        if (headerRowIndex !== null) {
            uploadUrl += `&header_row_index=${headerRowIndex}`;
        }

        try {
            const response = await axiosInstance.post<{
                headers: string[];
                gcs_object_name: string;
                file_type_received: UploadableFileType;
                preview: Record<string, string>;
            }>(uploadUrl, formData);

            const { headers, gcs_object_name, file_type_received, preview } = response.data;

            updateParentState({
                userHeaders: headers,
                previewData: preview || {},
                gcsObjectName: gcs_object_name,
                currentFileType: file_type_received,
                uploading: false,
                file: null,
                fileName: file.name,
                isMappedAndProcessed: false,
                showOverride: true,
            });

            if (savedMapping && Object.keys(savedMapping).length > 0) {
                const requiredFields = fieldsToMap.filter(f => f.required);
                const userColumnsInSavedMapping = Object.values(savedMapping);
                const missingHeaders = userColumnsInSavedMapping.filter(col => !headers.includes(col));

                if (missingHeaders.length === 0 && requiredFields.every(f => savedMapping[f.key])) {
                    updateParentState({ mapping: savedMapping, successMessage: 'Mapeo automático aplicado exitosamente.' });
                    setIsAutoMapped(true);
                    setMappingMismatch(null);
                } else {
                    const validSavedMapping = Object.fromEntries(
                        Object.entries(savedMapping).filter(([, userCol]) => headers.includes(userCol)),
                    );
                    updateParentState({ mapping: validSavedMapping, successMessage: 'Mapeo parcial aplicado. Por favor revise.' });
                    setIsAutoMapped(false);
                    setMappingMismatch('Advertencia: Algunas columnas de su mapeo guardado no se encontraron. Por favor, revise y complete el mapeo.');
                }
            } else {
                updateParentState({ successMessage: 'Archivo subido. Por favor, configure el mapeo.' });
            }
        } catch (err: any) {
            const detail = err.response?.data?.detail || err.message;
            updateParentState({ error: `Error al subir: ${detail}`, uploading: false });
        }
    };

    // HANDLERS (LÓGICA INTACTA + NORMALIZACIÓN)
    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || !event.target.files[0]) return;
        await startWithFile(event.target.files[0]);
    };

    // PRC-040: un archivo soltado en la zona común entra por el mismo camino.
    useEffect(() => {
        if (!incomingFile) return;
        onIncomingFileTaken?.();
        void startWithFile(incomingFile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incomingFile]);

    const startWithFile = async (selectedFile: File) => {

        updateParentState({
            ...initialSectionState,
            file: selectedFile,
            uploading: false,
            error: null,
            showOverride: true,
        });

        setIsAutoMapped(false);
        setIsReviewingMapping(false);
        setMappingMismatch(null);

        const fileName = selectedFile.name.toLowerCase();

        if (!fileName.endsWith('.xlsx')) {
            updateParentState({
                normalization: null,
                normalizationConfirmed: true,
                normalizationReviewOpen: false,
            });
            return;
        }

        updateParentState({ uploading: true });

        try {
            const normalizationResult = await analyzePricingFileNormalization(selectedFile, fileType as any);

            updateParentState({
                normalization: normalizationResult,
                normalizationSheetName: normalizationResult.selected_sheet,
                normalizationHeaderRowIndex: normalizationResult.header_row_index,
                normalizationHeaderRowInput: String(normalizationResult.header_row_index + 1),
                normalizationReviewOpen: true,
                normalizationConfirmed: false,
                uploading: false,
            });
        } catch (err: any) {
            const detail = err.response?.data?.detail || err.message;
            updateParentState({
                normalization: null,
                normalizationConfirmed: false,
                normalizationReviewOpen: false,
                uploading: false,
                error: `No se pudo analizar la estructura del archivo: ${detail}`,
            });
        }
    };

    const handleNormalizationSheetChange = (newSheetName: string) => {
        updateParentState({
            normalizationSheetName: newSheetName,
            error: null,
        });
    };

    const handleApplyNormalizationReview = async () => {
        if (!parentState.file || !parentState.normalization) return;

        const parsedHeaderRow = Number(parentState.normalizationHeaderRowInput);

        if (!Number.isInteger(parsedHeaderRow) || parsedHeaderRow < 1) {
            updateParentState({
                error: 'La fila de cabecera debe ser un número entero mayor o igual que 1.',
            });
            return;
        }

        const newIndex = parsedHeaderRow - 1;
        const sheetName = parentState.normalizationSheetName || parentState.normalization.selected_sheet;

        updateParentState({ uploading: true, error: null });

        try {
            const reviewResult = await reviewPricingFileNormalization(
                parentState.file,
                fileType as any,
                newIndex,
                sheetName,
            );

            updateParentState({
                normalization: reviewResult,
                normalizationSheetName: reviewResult.selected_sheet,
                normalizationHeaderRowIndex: reviewResult.header_row_index,
                normalizationHeaderRowInput: String(reviewResult.header_row_index + 1),
                uploading: false,
            });
        } catch (err: any) {
            const detail = err.response?.data?.detail || err.message;
            updateParentState({
                uploading: false,
                error: `No se pudo aplicar la revisión: ${detail}`,
            });
        }
    };

    const handleUpload = async (event: FormEvent) => {
        event.preventDefault();

        if (!parentState.file) {
            updateParentState({ error: 'Selecciona un archivo primero.' });
            return;
        }

        const sheetName = parentState.normalization?.selected_sheet ?? parentState.normalizationSheetName ?? null;
        const headerRowIndex = parentState.normalization
            ? parentState.normalization.header_row_index
            : null;

        await uploadConfirmedFile(parentState.file, sheetName, headerRowIndex);
    };
    
    const handleMappingChange = (event: SelectChangeEvent<string>, systemFieldKey: string) => {
        updateParentState({ mapping: { ...parentState.mapping, [systemFieldKey]: event.target.value }});
    };

    const handleConfirmMapping = () => {
        if (!parentState.gcsObjectName || !parentState.currentFileType) return;
        const payload = {
            file_type: parentState.currentFileType,
            mapping: Object.fromEntries(Object.entries(parentState.mapping).filter(([, v]) => v)),
            gcs_object_name: parentState.gcsObjectName,
            upload_mode: parentState.uploadMode,
        };
        processMutation.mutate(payload);
    };
    
    const state = parentState;
    const isLoading = isLoadingStatus || (state.showOverride && isLoadingMapping) || isLoadingTotalCount;
    const shouldShowPersistentStatus = !isLoading && currentStatusDetail && !state.showOverride;
    const canConfirmMapping = state.userHeaders.length > 0 && fieldsToMap.filter(f => f.required).every(f => state.mapping[f.key]);
    const isProcessingThisCard = processMutation.isPending && processMutation.variables?.file_type === fileType;
    const requiredFieldsForDetails = fieldsToMap.filter(f => f.required);
    const savedMappingEntriesForDetails = savedMapping
        ? Object.entries(savedMapping).filter(([, userColumn]) => Boolean(userColumn))
        : [];

    const parsedNormalizationHeaderRow = Number(state.normalizationHeaderRowInput);
    const draftNormalizationHeaderRowIndex =
        Number.isInteger(parsedNormalizationHeaderRow) && parsedNormalizationHeaderRow >= 1
            ? parsedNormalizationHeaderRow - 1
            : null;

    const hasPendingNormalizationReview = Boolean(
        state.normalization &&
        (
            state.normalizationSheetName !== state.normalization.selected_sheet ||
            draftNormalizationHeaderRowIndex !== state.normalization.header_row_index
        )
    );

    // El diálogo original se puede abrir desde Operaciones sin duplicar el formulario.
    // PRC-037: sin ventana «Preparando carga…»: el diálogo aparece cuando su estado está listo.
    if (modalOnly && isLoading && state.showOverride) {
        return null;
    }

    // --- RENDERIZADO VISUAL ACTUALIZADO (DARK PREMIUM) ---
    return (
        <Paper elevation={0} sx={modalOnly ? { ...glassCardSx, display: 'none' } : glassCardSx}>
            {/* PRC-040: el título y su explicación los pone la fila de la página (G5). */}
            
            {isLoading ? (
                // PRC-037: forma del bloque mientras carga, sin rueda.
                <SkeletonBlock h={140} />
            ) : shouldShowPersistentStatus && currentStatusDetail ? (
                // VISTA DE ESTADO PERSISTENTE (ARCHIVO YA CARGADO)
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Alert 
                        variant="outlined" 
                        severity={currentStatusDetail.status === 'success' ? 'success' : 'error'} 
                        icon={<CheckCircleIcon fontSize="inherit" />}
                        sx={{ 
                            mb: 3, 
                            textAlign: 'left', 
                            bgcolor: 'rgba(74, 222, 128, 0.05)', 
                            borderColor: '#4ade80',
                            color: '#f8fafc',
                            '& .MuiAlert-icon': { color: '#4ade80' }
                        }}
                    >
                        <AlertTitle sx={{ fontWeight: 700, color: '#4ade80' }}>
                            {fileType === 'transactions' ? 'Datos sincronizados' : 'Carga completada'}
                        </AlertTitle>
                        
                        <Typography variant="caption" display="block" sx={{ color: '#cbd5e1', mt: 1 }}>
                            {fileType === 'transactions' 
                                ? `Total Registros: ${toSpanishNumber(totalTransactionsData?.total_count, {}, '...')}`
                                : `Fecha: ${formatDateTime(currentStatusDetail.finished_at || undefined)}`
                            }
                        </Typography>

                        {fileType === 'transactions' ? (
                            <Typography variant="caption" display="block" sx={{ color: '#94a3b8', mt: 0.35 }}>
                                Cobertura temporal: {formatDateOnly(transactionDateRangeData?.min_date)}
                                <span style={{ margin: '0 6px', color: '#64748b' }}>→</span>
                                {formatDateOnly(transactionDateRangeData?.max_date)}
                            </Typography>
                        ) : (
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                Filas procesadas: {currentStatusDetail.rows_processed ?? 'N/A'}
                            </Typography>
                        )}
                    </Alert>
                    
                    <Stack spacing={1}>
                        <Button 
                            variant="outlined" 
                            size="small"
                            startIcon={<EditIcon />} 
                            onClick={() => updateParentState({ showOverride: true })}
                            sx={{ borderColor: '#334155', color: '#94a3b8', '&:hover': { borderColor: '#f8fafc', color: '#f8fafc' } }}
                        >
                            Actualizar datos
                        </Button>

                        <Button
                            variant="text"
                            size="small"
                            onClick={() => setShowDataDetails(prev => !prev)}
                            sx={{
                                color: '#38bdf8',
                                fontSize: '0.78rem',
                                textTransform: 'none',
                                alignSelf: 'center',
                                '&:hover': { bgcolor: 'rgba(56, 189, 248, 0.06)' },
                            }}
                        >
                            {showDataDetails ? 'Ocultar estructura y detalles' : 'Ver estructura y detalles'}
                        </Button>

                        <Collapse in={showDataDetails} timeout={250}>
                            <Box
                                sx={{
                                    mt: 1,
                                    p: 2,
                                    border: '1px solid #334155',
                                    borderRadius: 2,
                                    bgcolor: 'rgba(15, 23, 42, 0.35)',
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                color: '#94a3b8',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.6,
                                                mb: 0.75,
                                            }}
                                        >
                                            Campos requeridos
                                        </Typography>

                                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                            {requiredFieldsForDetails.map(field => (
                                                <Chip
                                                    key={field.key}
                                                    size="small"
                                                    label={field.label}
                                                    variant="outlined"
                                                    sx={{
                                                        borderColor: '#334155',
                                                        color: '#cbd5e1',
                                                        bgcolor: 'rgba(255,255,255,0.02)',
                                                        fontSize: '0.72rem',
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                color: '#94a3b8',
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.6,
                                                mb: 0.75,
                                            }}
                                        >
                                            Mapeo guardado
                                        </Typography>

                                        {savedMappingEntriesForDetails.length > 0 ? (
                                            <Stack spacing={0.5}>
                                                {savedMappingEntriesForDetails.map(([systemKey, userColumn]) => {
                                                    const field = fieldsToMap.find(item => item.key === systemKey);
                                                    return (
                                                        <Box
                                                            key={systemKey}
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: { xs: '1fr', sm: '180px 18px minmax(0, 220px)' },
                                                                alignItems: 'center',
                                                                columnGap: 1,
                                                                rowGap: 0.25,
                                                                fontSize: '0.78rem',
                                                            }}
                                                        >
                                                            <Typography variant="caption" sx={{ color: '#cbd5e1' }}>
                                                                {field?.label ?? systemKey}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    display: { xs: 'none', sm: 'block' },
                                                                    color: '#64748b',
                                                                    textAlign: 'center',
                                                                }}
                                                            >
                                                                →
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: '#38bdf8',
                                                                    fontWeight: 600,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {userColumn}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        ) : (
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                Todavía no hay un mapeo guardado para este tipo de datos.
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        </Collapse>
                    </Stack>
                </Box>
            ) : state.showOverride ? (
                // VISTA DE ACCIÓN (SUBIDA / MAPEO) DENTRO DE MODAL
                <Dialog
                    open
                    fullWidth
                    maxWidth="lg"
                    onClose={(_, reason) => {
                        if (reason === 'backdropClick' || state.uploading || isProcessingThisCard) return;
                        updateParentState({ ...initialSectionState, showOverride: false });
                    }}
                    PaperProps={{
                        sx: {
                            bgcolor: '#0f172a',
                            border: '1px solid #334155',
                            borderRadius: 3,
                            maxHeight: '88vh',
                            backgroundImage: 'none',
                        }
                    }}
                >
                    <DialogTitle
                        sx={{
                            color: '#f8fafc',
                            fontWeight: 800,
                            borderBottom: '1px solid #334155',
                            px: 3,
                            py: 2.25,
                        }}
                    >
                        Actualizar {title}
                    </DialogTitle>

                    <DialogContent
                        dividers
                        sx={{
                            bgcolor: '#0f172a',
                            borderColor: '#334155',
                            px: { xs: 2, md: 3 },
                            py: 3,
                        }}
                    >
                        <Box sx={{ flexGrow: 1 }}>
                     {isProcessingThisCard && <LinearProgress sx={{ my: 2, bgcolor: 'rgba(56,189,248,0.2)', '& .MuiLinearProgress-bar': { bgcolor: '#38bdf8' } }} />}
                     
                     {state.error && (
                         <Alert severity="error" variant="outlined" sx={{ my: 2, borderColor: '#ef4444', color: '#fca5a5' }}>
                             {state.error}
                         </Alert>
                     )}
                     
                     {state.successMessage && !state.isMappedAndProcessed && (
                        <Alert 
                            severity="info" 
                            variant="outlined"
                            sx={{ my: 2, borderColor: '#38bdf8', color: '#bae6fd', '& .MuiAlert-icon': { color: '#38bdf8' } }}
                            action={isAutoMapped ? <Button size="small" onClick={() => setIsReviewingMapping(true)} sx={{ color: '#38bdf8' }}>Verificar/Modificar</Button> : null}
                        >
                            {state.successMessage}
                        </Alert>
                     )}
                    
                    {/* PANEL DE NORMALIZACIÓN */}
                    {state.file && state.normalization && state.normalizationReviewOpen && !state.userHeaders.length && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 2 }}>
                            <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" sx={{ color: '#38bdf8', fontWeight: 700 }}>
                                        Configuración del archivo
                                    </Typography>
                                    <Chip 
                                        size="small" 
                                        label={state.normalization.confidence === 'high' ? 'Detección automática' : 'Revisión recomendada'} 
                                        color={state.normalization.confidence === 'high' ? 'success' : 'warning'}
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                </Stack>

                                {/* Selector de hoja — solo si hay más de una */}
                                {state.normalization.sheets.length > 1 && (
                                    <Stack spacing={0.5}>
                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>Hoja activa</Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {state.normalization.sheets.map((sheet) => (
                                                <Chip
                                                    key={sheet.name}
                                                    label={`${sheet.name} (${sheet.row_count} filas)`}
                                                    size="small"
                                                    onClick={() => handleNormalizationSheetChange(sheet.name)}
                                                    color={state.normalizationSheetName === sheet.name ? 'primary' : 'default'}
                                                    variant={state.normalizationSheetName === sheet.name ? 'filled' : 'outlined'}
                                                    sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>
                                )}

                                {/* Selector local e instantáneo de fila de cabecera */}
                                <Stack spacing={1}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Typography variant="caption" sx={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                            Fila de cabecera
                                        </Typography>

                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ minWidth: 28, px: 0.5, borderColor: '#334155', color: '#94a3b8' }}
                                                disabled={
                                                    state.uploading ||
                                                    draftNormalizationHeaderRowIndex === null ||
                                                    draftNormalizationHeaderRowIndex <= 0
                                                }
                                                onClick={() => {
                                                    const currentIndex = draftNormalizationHeaderRowIndex ?? 0;
                                                    updateParentState({
                                                        normalizationHeaderRowInput: String(Math.max(0, currentIndex - 1) + 1),
                                                        error: null,
                                                    });
                                                }}
                                            >
                                                -
                                            </Button>

                                            <Typography sx={{ color: '#f8fafc', minWidth: 24, textAlign: 'center', fontSize: '0.9rem' }}>
                                                {state.normalizationHeaderRowInput}
                                            </Typography>

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ minWidth: 28, px: 0.5, borderColor: '#334155', color: '#94a3b8' }}
                                                disabled={state.uploading}
                                                onClick={() => {
                                                    const currentIndex = draftNormalizationHeaderRowIndex ?? 0;
                                                    updateParentState({
                                                        normalizationHeaderRowInput: String(currentIndex + 2),
                                                        error: null,
                                                    });
                                                }}
                                            >
                                                +
                                            </Button>
                                        </Stack>
                                    </Stack>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={state.uploading || !hasPendingNormalizationReview}
                                            onClick={() => void handleApplyNormalizationReview()}
                                            sx={{
                                                alignSelf: 'flex-start',
                                                borderColor: '#38bdf8',
                                                color: '#38bdf8',
                                                '&:hover': {
                                                    borderColor: '#67e8f9',
                                                    bgcolor: 'rgba(56, 189, 248, 0.06)',
                                                },
                                            }}
                                        >
                                            {state.uploading ? 'Aplicando...' : 'Aplicar revisión'}
                                        </Button>

                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                            {hasPendingNormalizationReview
                                                ? 'Selección local pendiente de aplicar.'
                                                : 'Configuración aplicada.'}
                                        </Typography>
                                    </Stack>
                                </Stack>

                                {/* Preview crudo para revisar instantáneamente la fila candidata */}
                                {state.normalization.raw_preview_rows.length > 0 && (
                                    <Stack spacing={0.75}>
                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                            Vista previa cruda de la hoja
                                        </Typography>

                                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                                            La fila resaltada se actualiza localmente. Pulse “Aplicar revisión” únicamente cuando la selección sea correcta.
                                        </Typography>

                                        {state.normalizationSheetName !== state.normalization.selected_sheet && (
                                            <Alert
                                                severity="info"
                                                variant="outlined"
                                                sx={{
                                                    py: 0.25,
                                                    borderColor: 'rgba(56, 189, 248, 0.35)',
                                                    color: '#bae6fd',
                                                    '& .MuiAlert-icon': { color: '#38bdf8' },
                                                }}
                                            >
                                                Aplique la revisión para cargar el contenido de la nueva hoja seleccionada.
                                            </Alert>
                                        )}

                                        <TableContainer
                                            sx={{
                                                maxHeight: 260,
                                                overflowX: 'auto',
                                                border: '1px solid #334155',
                                                borderRadius: 1.5,
                                                bgcolor: 'rgba(2, 6, 23, 0.22)',
                                            }}
                                        >
                                            <Table size="small" stickyHeader>
                                                <TableBody>
                                                    {state.normalization.raw_preview_rows.slice(0, 12).map((row, rowIndex) => {
                                                        const isCandidateHeader = draftNormalizationHeaderRowIndex === rowIndex;

                                                        return (
                                                            <TableRow
                                                                key={rowIndex}
                                                                sx={{
                                                                    bgcolor: isCandidateHeader
                                                                        ? 'rgba(56, 189, 248, 0.12)'
                                                                        : 'transparent',
                                                                }}
                                                            >
                                                                <TableCell
                                                                    sx={{
                                                                        minWidth: 86,
                                                                        color: isCandidateHeader ? '#38bdf8' : '#64748b',
                                                                        fontWeight: isCandidateHeader ? 800 : 500,
                                                                        fontSize: '0.7rem',
                                                                        borderColor: '#1e293b',
                                                                        whiteSpace: 'nowrap',
                                                                        py: 0.5,
                                                                        px: 1,
                                                                    }}
                                                                >
                                                                    {isCandidateHeader ? 'CABECERA' : `Fila ${rowIndex + 1}`}
                                                                </TableCell>

                                                                {(row as unknown[]).map((cell, cellIndex) => (
                                                                    <TableCell
                                                                        key={cellIndex}
                                                                        sx={{
                                                                            color: isCandidateHeader ? '#e2e8f0' : '#94a3b8',
                                                                            fontWeight: isCandidateHeader ? 700 : 400,
                                                                            fontSize: '0.72rem',
                                                                            borderColor: '#1e293b',
                                                                            whiteSpace: 'nowrap',
                                                                            py: 0.5,
                                                                            px: 1,
                                                                        }}
                                                                    >
                                                                        {String(cell ?? '').substring(0, 26)}
                                                                        {String(cell ?? '').length > 26 ? '…' : ''}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Stack>
                                )}

                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={state.uploading || hasPendingNormalizationReview}
                                    sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700, '&:hover': { bgcolor: '#0ea5e9' }, alignSelf: 'flex-start' }}
                                    onClick={() => {
                                        const confirmedSheetName = state.normalization?.selected_sheet ?? null;
                                        const confirmedHeaderRowIndex = state.normalization?.header_row_index ?? 0;

                                        updateParentState({
                                            normalizationConfirmed: true,
                                            normalizationReviewOpen: false,
                                            normalizationHeaderRowIndex: confirmedHeaderRowIndex,
                                            normalizationSheetName: confirmedSheetName,
                                        });

                                        if (state.file) {
                                            void uploadConfirmedFile(
                                                state.file,
                                                confirmedSheetName,
                                                confirmedHeaderRowIndex,
                                            );
                                        }
                                    }}
                                >
                                    Usar esta configuración
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    {/* Chip informativo cuando la normalización es automática y está confirmada */}
                    {state.file && state.normalization && !state.normalizationReviewOpen && !state.userHeaders.length && state.normalizationConfirmed && state.normalization.sheets.length > 1 && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <Chip size="small" label={`Hoja: ${state.normalizationSheetName}`} variant="outlined" sx={{ fontSize: '0.72rem', borderColor: '#334155', color: '#94a3b8' }} />
                            <Chip size="small" label={`Cabecera: fila ${state.normalizationHeaderRowIndex + 1}`} variant="outlined" sx={{ fontSize: '0.72rem', borderColor: '#334155', color: '#94a3b8' }} />
                            <Button size="small" variant="text" sx={{ color: '#64748b', fontSize: '0.72rem', py: 0 }} onClick={() => updateParentState({ normalizationReviewOpen: true, normalizationConfirmed: false })}>
                                Ajustar
                            </Button>
                        </Stack>
                    )}

                    {!state.userHeaders.length && !state.normalizationReviewOpen ? (
                        // ZONA DE CARGA (DROPZONE VISUAL)
                        <Box component="form" onSubmit={handleUpload} sx={{ height: '100%' }}>
                            <Box component="label" sx={uploadZoneSx}>
                                <CloudUploadIcon sx={{ fontSize: 40, color: state.uploading ? '#64748b' : '#38bdf8' }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                                        {state.uploading ? 'Subiendo...' : 'Haga clic para cargar'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                        {state.file ? state.file.name : 'Soporta .CSV o .XLSX'}
                                    </Typography>
                                </Box>
                                <VisuallyHiddenInput type="file" onChange={handleFileChange} disabled={state.uploading} />
                            </Box>
                            
                            {state.file && !state.normalization && (
                                <Button 
                                    type="submit" 
                                    fullWidth 
                                    variant="contained" 
                                    disabled={state.uploading}
                                    sx={{ mt: 2, bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700, '&:hover': { bgcolor: '#0ea5e9' } }}
                                >
                                    {state.uploading ? 'Procesando...' : 'Iniciar Carga'}
                                </Button>
                            )}
                            {state.file && state.normalization && state.uploading && (
                                <Button 
                                    fullWidth 
                                    variant="contained" 
                                    disabled
                                    sx={{ mt: 2, bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700 }}
                                >
                                    Procesando...
                                </Button>
                            )}
                        </Box>
                    ) : state.userHeaders.length ? (
                        // ZONA DE CONFIGURACIÓN (MAPEO)
                        <Box sx={{ animation: 'fadeIn 0.3s ease-in' }}>
                            {!state.processingResult ? (
                                <>
                                    <Box sx={{ my: 3, p: 2, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2, border: '1px dashed #334155' }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#94a3b8' }}>MODO DE ACTUALIZACIÓN</Typography>
                                        {isExpandedMode && (
                                            <Alert 
                                                severity="warning" 
                                                variant="outlined" 
                                                icon={<WarningAmberIcon fontSize="inherit" />}
                                                sx={{ mb: 2, borderColor: '#fcd34d', color: '#fcd34d', '& .MuiAlert-icon': { color: '#fcd34d' } }}
                                            >
                                                <AlertTitle>Modificación de Estructura</AlertTitle>
                                                Al añadir o modificar campos estructurales, se requiere un <strong>Reemplazo Total</strong> para mantener la integridad de la base de datos.
                                            </Alert>
                                        )}
                                        <FormControl component="fieldset">
                                            <RadioGroup row value={parentState.uploadMode} onChange={(e) => updateParentState({ uploadMode: e.target.value as any })}>
                                                {fileType === 'transactions' ? ( 
                                                    /* CASO 1: TRANSACCIONES (BLINDADO) */
                                                    <>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                                                            <FormControlLabel 
                                                                value="replace_by_date_range"
                                                                disabled={isExpandedMode} 
                                                                control={<Radio size="small" sx={{ color: '#64748b', '&.Mui-checked': { color: '#38bdf8' }, '&.Mui-disabled': { color: '#334155' } }} />} 
                                                                label={
                                                                    <Typography variant="body2" color={isExpandedMode ? "#475569" : "#cbd5e1"}>
                                                                        Por Rango (Inteligente)
                                                                    </Typography>
                                                                } 
                                                            />
                                                            {isExpandedMode && (
                                                                <Tooltip title="Deshabilitado por cambio de estructura">
                                                                    <InfoIcon sx={{ fontSize: 16, color: '#475569', ml: -0.5, mt: 0.5 }} />
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                        <FormControlLabel value="replace" control={<Radio size="small" sx={{ color: '#64748b', '&.Mui-checked': { color: '#f87171' } }} />} label={<Typography variant="body2" color="#cbd5e1">Reemplazo Total</Typography>} />
                                                    </> 
                                                ) : ( 
                                                    /* CASO 2: PRODUCTOS / CLIENTES / COSTES (BLINDADO) */
                                                    <>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                                                            <FormControlLabel 
                                                                value="incremental" 
                                                                disabled={isExpandedMode}
                                                                control={<Radio size="small" sx={{ color: '#64748b', '&.Mui-checked': { color: '#38bdf8' }, '&.Mui-disabled': { color: '#334155' } }} />} 
                                                                label={
                                                                    <Typography variant="body2" color={isExpandedMode ? "#475569" : "#cbd5e1"}>
                                                                        Incremental
                                                                    </Typography>
                                                                } 
                                                            />
                                                            {isExpandedMode && (
                                                                <Tooltip title="Deshabilitado por cambio de estructura">
                                                                    <InfoIcon sx={{ fontSize: 16, color: '#475569', ml: -0.5, mt: 0.5 }} />
                                                                </Tooltip>
                                                            )}
                                                        </Box>

                                                        <FormControlLabel value="replace" control={<Radio size="small" sx={{ color: '#64748b', '&.Mui-checked': { color: '#f87171' } }} />} label={<Typography variant="body2" color="#cbd5e1">Reemplazo Total</Typography>} />
                                                    </> 
                                                )}
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                    
                                    {mappingMismatch && <Alert severity="warning" variant="outlined" sx={{ mb: 2, borderColor: '#fcd34d', color: '#fcd34d' }}>{mappingMismatch}</Alert>}

                                    {(!isAutoMapped || isReviewingMapping) && (
                                        <>
                                            {/* TABLA DINÁMICA: Solo muestra campos visibles */}
                                            <TableContainer sx={{ border: '1px solid #334155', borderRadius: 2 }}>
                                                <Table stickyHeader size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>CAMPO SISTEMA</TableCell>
                                                            <TableCell sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>TU ARCHIVO</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {visibleFields.map(field => (
                                                            <TableRow key={field.key}>
                                                                <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid rgba(51, 65, 85, 0.5)' }}>
                                                                    {field.label} {field.required && <span style={{ color: '#f87171' }}>*</span>}
                                                                </TableCell>
                                                                <TableCell sx={{ borderBottom: '1px solid rgba(51, 65, 85, 0.5)' }}>
                                                                    <FormControl fullWidth size="small" sx={stealthSelectSx}>
                                                                        <Select 
                                                                            value={state.mapping[field.key] || ''} 
                                                                            onChange={e => handleMappingChange(e, field.key)}
                                                                            displayEmpty
                                                                        >
                                                                        
                                                                            <MenuItem value="">
                                                                                <Typography variant="caption" color="text.secondary">Ignorar</Typography>
                                                                            </MenuItem>
                                                                            {state.userHeaders.map(h => (
                                                                                <MenuItem key={h} value={h}>{h}</MenuItem>
                                                                            ))}
                                                                        </Select>
                                                                        {/* --- PREVIEW VISUAL --- */}
                                                                        {state.mapping[field.key] && state.previewData[state.mapping[field.key]] && (
                                                                            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#64748b', fontStyle: 'italic' }}>
                                                                                Ej: {state.previewData[state.mapping[field.key]].substring(0, 30)}
                                                                                {state.previewData[state.mapping[field.key]].length > 30 ? '...' : ''}
                                                                            </Typography>
                                                                        )}
                                                                    </FormControl>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            
                                            {/* BOTONES DE CONTROL DE ESTRUCTURA */}
                                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                                {!isExpandedMode && fieldsToMap.length > visibleFields.length ? (
                                                    <Button 
                                                        size="small" 
                                                        variant="text" 
                                                        startIcon={<EditIcon />}
                                                        onClick={handleToggleExpand}
                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#f8fafc', bgcolor: 'rgba(255,255,255,0.05)' } }}
                                                    >
                                                        Modificar Estructura / Añadir Campos Nuevos
                                                    </Button>
                                                ) : isExpandedMode && (
                                                    <Button 
                                                        size="small" 
                                                        variant="outlined" 
                                                        color="warning"
                                                        onClick={() => {
                                                            setIsExpandedMode(false);
                                                            // REVERTIR A ESTADO SEGURO: Si había mapeo guardado, volvemos a él
                                                            if (savedMapping) {
                                                                updateParentState({ mapping: savedMapping });
                                                                // Restauramos la opción incremental por defecto si es seguro
                                                                updateParentState({ uploadMode: 'incremental', mapping: savedMapping }); 
                                                            } else {
                                                                // Si no había mapeo previo, simplemente cerramos, pero mantenemos replace por precaución 
                                                                // o limpiamos los campos nuevos.
                                                                setIsExpandedMode(false);
                                                            }
                                                        }}
                                                        sx={{ borderColor: '#fcd34d', color: '#fcd34d', '&:hover': { borderColor: '#fbbf24', bgcolor: 'rgba(251, 191, 36, 0.05)' } }}
                                                    >
                                                        Cancelar Edición y Restaurar Estructura
                                                    </Button>
                                                )}
                                            </Box>
                                        </>
                                    )}
                                    
                                    <Button 
                                        variant="contained" 
                                        fullWidth
                                        sx={{ mt: 3, bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700, '&:hover': { bgcolor: '#0ea5e9' } }}
                                        onClick={handleConfirmMapping} 
                                        disabled={!canConfirmMapping || processMutation.isPending}
                                    >
                                        {processMutation.isPending ? 'Procesando...' : 'Confirmar e Ingestar'}
                                    </Button>
                                </>
                            ) : (
                            // VISTA DE ÉXITO (INFORME DE IMPACTO PREMIUM)
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <CheckCircleIcon sx={{ fontSize: 60, color: '#4ade80', mb: 2 }} />
                                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                                    Carga completada
                                </Typography>
                                
                                {/* TARJETA DE ESTADÍSTICAS */}
                                <Box sx={{ mt: 3, mb: 3, p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Registros</Typography>
                                        <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                                            {toSpanishNumber(state.processingResult.row_count)}
                                        </Typography>
                                    </Box>
                                    
                                    {/* Solo mostrar fechas si existen en el perfil (para transacciones) */}
                                    {state.processingResult.column_profiles['transaction_date'] && (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>Cobertura Temporal</Typography>
                                            <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                                                {state.processingResult.column_profiles['transaction_date'].min_date?.split('T')[0] || 'N/A'} 
                                                <span style={{ margin: '0 8px', color: '#64748b' }}>➔</span>
                                                {state.processingResult.column_profiles['transaction_date'].max_date?.split('T')[0] || 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
                                    Los datos han sido validados e integrados en el motor de análisis.
                                </Typography>
                                
                                <Stack direction="row" spacing={2} justifyContent="center">
                                    <Button variant="outlined" color="primary" onClick={() => navigate(withReturnTo('/data-quality', currentOrigin(location)))}>
                                        Ver calidad detallada
                                    </Button>
                                    <Button variant="text" sx={{ color: '#94a3b8' }} onClick={() => updateParentState({ ...initialSectionState, showOverride: false })}>
                                        Cerrar
                                    </Button>
                                </Stack>
                            </Box>
                        )}
                        </Box>
                    ) : null}
                        </Box>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            bgcolor: '#0f172a',
                            borderTop: '1px solid #334155',
                            px: 3,
                            py: 1.75,
                        }}
                    >
                        <Button
                            variant="text"
                            disabled={state.uploading || isProcessingThisCard}
                            onClick={() => updateParentState({ ...initialSectionState, showOverride: false })}
                            sx={{ color: '#94a3b8' }}
                        >
                            Cerrar
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        minHeight: 112,
                        p: 2,
                        borderRadius: 2,
                        border: '1px dashed #334155',
                        bgcolor: 'rgba(15, 23, 42, 0.28)',
                    }}
                >
                    <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 700 }}>
                        Todavía no hay datos cargados
                    </Typography>

                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Seleccione un archivo CSV o XLSX para iniciar la configuración.
                    </Typography>

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => updateParentState({ showOverride: true })}
                        sx={{
                            mt: 0.5,
                            borderColor: '#334155',
                            color: '#38bdf8',
                            '&:hover': {
                                borderColor: '#38bdf8',
                                bgcolor: 'rgba(56, 189, 248, 0.05)',
                            },
                        }}
                    >
                        Iniciar carga
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

// --- COMPONENTE PADRE (DARK PREMIUM LAYOUT) ---
type ModalSource = 'customers' | 'products' | 'transactions';
interface DataUploadPageProps {
    modalSource?: ModalSource;
    onModalClosed?: () => void;
}
const DataUploadPage: FC<DataUploadPageProps> = ({ modalSource, onModalClosed }) => {
    const [selectedStrategy] = useState<CostStrategy>('direct');
    const [uploadStates, setUploadStates] = useState<Record<UploadableFileType, UploadSectionState>>({
        customers: { ...initialSectionState, showOverride: modalSource === 'customers' },
        products: { ...initialSectionState, showOverride: modalSource === 'products' },
        transactions: { ...initialSectionState, showOverride: modalSource === 'transactions' },
        historical_costs: { ...initialSectionState },
    });
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    // PRC-040: archivo reconocido por columnas y último resultado de cada tipo.
    const [incomingFiles, setIncomingFiles] = useState<Partial<Record<UploadableFileType, File | null>>>({});
    const [detectError, setDetectError] = useState<string | null>(null);
    const [detecting, setDetecting] = useState(false);
    const [lastResults, setLastResults] = useState<Partial<Record<UploadableFileType, { fileName: string | null; at: string; processed: number; discards?: DiscardSummaryData; salesLimit?: { limit: number; not_loaded: number } | null; productsWithoutCost?: number | null; costBackfill?: { updated: number } | null }>>>({});
    const { data: salesCount } = useQuery({ queryKey: ['transactionCount'], queryFn: getTransactionCount, staleTime: 60_000 });
    const salesLines = salesCount?.total_count ?? 0;

    const updateSingleState = (fileType: UploadableFileType) => (newState: Partial<UploadSectionState>) => {
        setUploadStates(prev => ({ ...prev, [fileType]: { ...prev[fileType], ...newState } }));
    };

    useEffect(() => {
        if (modalSource && !uploadStates[modalSource].showOverride) {
            onModalClosed?.();
        }
    }, [modalSource, onModalClosed, uploadStates]);

    const processMutation = useMutation<AxiosResponse, AxiosError<{ detail: string }>, ProcessPayload>({
        mutationFn: (payload) => {
            const finalPayload = { ...payload };
            if (payload.file_type === 'transactions') {
                finalPayload.cost_strategy = selectedStrategy;
            }
            return axiosInstance.post('/data/process', finalPayload);
        },
        onSuccess: async (response, variables) => {
            const fileType = variables.file_type;
            const profile: DataProfileResponse | undefined = response.data.profile;
            // PRC-040: descartes, límite de líneas y costes rellenados.
            setLastResults((prev) => ({
                ...prev,
                [fileType]: {
                    fileName: uploadStates[fileType as UploadableFileType]?.fileName ?? null,
                    at: new Date().toISOString(),
                    processed: response.data.processed_rows ?? 0,
                    discards: response.data.discards,
                    salesLimit: response.data.sales_limit,
                    productsWithoutCost: response.data.products_without_cost,
                    costBackfill: response.data.cost_backfill,
                },
            }));
            if (fileType === 'transactions') {
                // Margin Center se abre con los datos al terminar; el motor se prepara aparte.
                void queryClient.invalidateQueries({ queryKey: ['retraining-status'] });
                void queryClient.invalidateQueries({ queryKey: ['margin-center'] });
            }

            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['dataStatus', fileType] }),
                queryClient.invalidateQueries({ queryKey: ['savedMapping', fileType] }),
                queryClient.invalidateQueries({ queryKey: ['operations'] }),
                fileType === 'transactions'
                    ? queryClient.invalidateQueries({ queryKey: ['transactionCount'] })
                    : Promise.resolve(),
                fileType === 'transactions'
                    ? queryClient.invalidateQueries({ queryKey: ['transactionDateRange'] })
                    : Promise.resolve(),
            ]);

            if (profile) {
                enqueueSnackbar(`Diagnóstico de datos completado para ${fileType}`, { variant: 'success' });
                updateSingleState(fileType)({
                    processingResult: profile,
                    isMappedAndProcessed: true,
                    error: null,
                    uploading: false,
                });
            } else {
                enqueueSnackbar(response.data.message || `Proceso para ${fileType} completado`, { variant: 'success' });
                updateSingleState(fileType)({ ...initialSectionState, showOverride: false });
            }
        },
        onError: (error, variables) => {
            const fileType = variables.file_type;
            const detail = error.response?.data?.detail || 'Error desconocido.';
            enqueueSnackbar(`Error en procesamiento (${fileType}): ${detail}`, { variant: 'error' });
            updateSingleState(fileType)({ error: `Error en procesamiento: ${detail}`, uploading: false });
        },
    });

    const handleDownloadExample = (type: UploadableFileType) => {
        let headers: string[] = [];
        let data: (string | number)[][] = [];
        const filename = `${type}_ejemplo.xlsx`;
        switch(type) {
            case 'customers':
                headers = ['customer_id', 'nombre', 'segmento'];
                data = [['CUST-001', 'Cliente Ejemplo 1', 'Retail'], ['CUST-002', 'Cliente Ejemplo 2', 'Mayorista']];
                break;
            case 'products':
                headers = ['product_id', 'nombre_producto', 'categoria', 'standard_cost'];
                data = [['PROD-A', 'Producto A', 'Electrónica', 150.75], ['PROD-B', 'Producto B', 'Hogar', 45.50]];
                break;
            case 'transactions':
                headers = ['transaction_date', 'customer_id', 'product_id', 'quantity', 'unit_price', 'discount_percentage', 'unit_cost'];
                data = [['2025-07-28', 'CUST-001', 'PROD-A', 2, 299.99, 10, 150.75], ['2025-07-29', 'CUST-002', 'PROD-B', 5, 59.99, 0, 45.50]];
                break;
            case 'historical_costs':
                headers = ['product_id', 'unit_cost', 'valid_from', 'valid_to'];
                data = [['PROD-A', 145.00, '2024-01-01', '2024-12-31'], ['PROD-A', 155.50, '2025-01-01', '2025-12-31']];
                break;
        }
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Datos de Ejemplo");
        XLSX.writeFile(workbook, filename);
    };

    // Estado de cada archivo: lo cargado en esta visita o la última carga correcta guardada.
    const savedStatus = useQueries({
        queries: (['transactions', 'customers', 'products'] as UploadableFileType[]).map((type) => ({
            queryKey: ['dataStatus', type],
            queryFn: () => getDataStatus(type as FileType),
            staleTime: 5 * 60 * 1000,
            retry: false,
            refetchOnWindowFocus: false,
        })),
    });
    const savedOf = (type: UploadableFileType) => {
        const status = savedStatus[['transactions', 'customers', 'products'].indexOf(type)]?.data;
        return status && status.status === 'success' ? status : null;
    };
    if (modalSource) {
        const title = modalSource === 'customers' ? 'Clientes' : modalSource === 'products' ? 'Productos' : 'Ventas';
        const subtitle = modalSource === 'customers' ? 'Directorio de cuentas activas.' : modalSource === 'products' ? 'Catálogo y estructura de costes.' : 'El motor utilizará el coste unitario incluido en cada línea.';
        return (
            <UploadSection
                modalOnly
                fileType={modalSource}
                title={title}
                subtitle={subtitle}
                parentState={uploadStates[modalSource]}
                updateParentState={updateSingleState(modalSource)}
                processMutation={processMutation}
                fieldsToMap={SYSTEM_FIELDS[modalSource]}
            />
        );
    }

    // PRC-040: el tipo se reconoce por las columnas; no hace falta elegirlo antes.
    const handleDetectedFile = async (file: File) => {
        setDetectError(null);
        setDetecting(true);
        try {
            const detected = await detectFileType(file);
            if (!detected) {
                setDetectError('No reconocemos el tipo de este archivo por sus columnas. Súbelo desde la fila de Ventas, Clientes o Productos.');
                return;
            }
            setIncomingFiles((prev) => ({ ...prev, [detected]: file }));
            document.getElementById(`carga-${detected}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch {
            setDetectError('No hemos podido leer el archivo. Comprueba que es CSV o Excel.');
        } finally {
            setDetecting(false);
        }
    };

    const salesRatio = salesLines / PLAN_LIMITS.salesLines;
    const salesResult = lastResults.transactions;
    const isLoaded = (type: UploadableFileType) => !!lastResults[type] || !!savedOf(type);
    const rowStatus = (type: UploadableFileType) => (isLoaded(type) ? 'Cargado' : type === 'transactions' ? 'Obligatorio' : 'Opcional');

    const fileRow = (type: UploadableFileType, title: string, text: string, fields: SystemField[]) => (
        <Box id={`carga-${type}`} sx={{ scrollMarginTop: 16, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Typography component="h2" sx={{ color: '#f8fafc', fontWeight: 800, fontSize: 17 }}>{title}</Typography>
                <JourneyChip tone={isLoaded(type) ? 'ok' : type === 'transactions' ? 'sky' : 'neutral'}>{rowStatus(type)}</JourneyChip>
            </Box>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>{text}</Typography>
            {lastResults[type] && (
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    {[
                        lastResults[type]!.fileName,
                        `${toSpanishNumber(lastResults[type]!.processed)} líneas válidas`,
                        lastResults[type]!.discards?.total ? `${toSpanishNumber(lastResults[type]!.discards!.total)} descartadas` : null,
                        new Date(lastResults[type]!.at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                    ].filter(Boolean).join(' · ')}
                </Typography>
            )}
            {!lastResults[type] && savedOf(type) && (
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    {[
                        savedOf(type)!.original_filename,
                        savedOf(type)!.rows_processed != null ? `${toSpanishNumber(savedOf(type)!.rows_processed!)} líneas` : null,
                        savedOf(type)!.finished_at ? `cargado el ${new Date(savedOf(type)!.finished_at!).toLocaleDateString('es-ES')}` : null,
                    ].filter(Boolean).join(' · ')}
                </Typography>
            )}
            <UploadSection
                fileType={type}
                title={title}
                subtitle={text}
                parentState={uploadStates[type]}
                updateParentState={updateSingleState(type)}
                processMutation={processMutation}
                fieldsToMap={fields}
                incomingFile={incomingFiles[type] ?? null}
                onIncomingFileTaken={() => setIncomingFiles((prev) => ({ ...prev, [type]: null }))}
            />
            {lastResults[type]?.discards && <DiscardSummary data={lastResults[type]!.discards!} fileName={lastResults[type]!.fileName} />}
            {type === 'products' && (lastResults.products?.productsWithoutCost ?? 0) > 0 && (
                <Notice tone="info">{toSpanishNumber(lastResults.products!.productsWithoutCost!)} productos se han cargado sin coste estándar (vacío o ≤ 0).</Notice>
            )}
            {type !== 'transactions' && (lastResults[type]?.costBackfill?.updated ?? 0) > 0 && (
                <Notice tone="ok">{toSpanishNumber(lastResults[type]!.costBackfill!.updated)} líneas de venta sin coste han tomado el coste nuevo. Margin Center ya lo muestra.</Notice>
            )}
        </Box>
    );

    return (
        <Box sx={pageFrameSx}>
            {/* PRC-040: carga de solo ventas (boceto G5). */}
            <OperationsDetailHeader
                eyebrow="Datos"
                title="Carga de datos"
                description="Solo las ventas son obligatorias"
                action={(
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <JourneyButton kind="ghost" small onClick={() => handleDownloadExample('transactions')}>Plantilla de ventas</JourneyButton>
                        <JourneyButton kind="ghost" small onClick={() => handleDownloadExample('customers')}>Clientes</JourneyButton>
                        <JourneyButton kind="ghost" small onClick={() => handleDownloadExample('products')}>Productos</JourneyButton>
                    </Box>
                )}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) 420px' }, gap: 3, alignItems: 'start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    {/* Zona común: se reconoce el tipo por las columnas. */}
                    <Box
                        component="label"
                        onDragOver={(e: React.DragEvent) => e.preventDefault()}
                        onDrop={(e: React.DragEvent) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) void handleDetectedFile(f); }}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 4, textAlign: 'center', cursor: 'pointer', border: `1px dashed ${J.borderStrong}`, borderRadius: '16px', bgcolor: 'rgba(15,23,42,.5)', '&:hover': { borderColor: J.sky } }}
                    >
                        <CloudUploadIcon sx={{ fontSize: 32, color: '#94a3b8' }} />
                        <Typography sx={{ color: '#f8fafc', fontWeight: 700 }}>{detecting ? 'Leyendo el archivo…' : 'Arrastra aquí un archivo o pulsa para elegirlo'}</Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>CSV o Excel. Reconocemos el tipo por las columnas; no hace falta elegirlo antes.</Typography>
                        <Box component="input" type="file" accept=".csv,.xlsx" hidden onChange={(e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) void handleDetectedFile(f); e.target.value = ''; }} />
                    </Box>
                    {detectError && <Notice tone="warn">{detectError}</Notice>}

                    {/* PRC-040: límite de líneas de venta del plan: aviso al 80 % y al llegar. */}
                    {salesRatio >= 0.8 && (
                        <Notice tone={salesRatio >= 1 ? 'danger' : 'warn'}>
                            {salesRatio >= 1
                                ? `Has llegado al límite de ${toSpanishNumber(PLAN_LIMITS.salesLines)} líneas de venta del plan. Las líneas nuevas no entran hasta que liberes espacio (por ejemplo, reemplazando el histórico).`
                                : `Llevas ${toSpanishNumber(salesLines)} de ${toSpanishNumber(PLAN_LIMITS.salesLines)} líneas de venta (${Math.round(salesRatio * 100)} %).`}
                        </Notice>
                    )}
                    {(salesResult?.salesLimit?.not_loaded ?? 0) > 0 && (
                        <Notice tone="danger">
                            {toSpanishNumber(salesResult!.salesLimit!.not_loaded)} líneas no han entrado porque se supera el límite de {toSpanishNumber(salesResult!.salesLimit!.limit)} líneas de venta del plan. Se han cargado las más recientes.
                        </Notice>
                    )}

                    {fileRow('transactions', 'Ventas', 'Tu histórico de ventas: con él ves tu margen y se prepara el motor de precios.', SYSTEM_FIELDS.transactions)}
                    {fileRow('customers', 'Clientes', 'Añade nombre comercial, segmento y zona. Sin él usamos el código y el nombre que vengan en las ventas.', SYSTEM_FIELDS.customers)}
                    {fileRow('products', 'Productos', 'Añade familia y coste estándar. Sin él usamos el coste de cada línea de venta.', SYSTEM_FIELDS.products)}
                </Box>

                {/* Preparando tu motor de precios (PRC-041): solo cuando ya hay ventas. */}
                <Box sx={{ position: { xl: 'sticky' }, top: { xl: 16 } }}>
                    <EnginePanel
                        fileName={salesResult?.fileName}
                        receivedAt={salesResult?.at}
                        validLines={salesResult?.processed ?? (salesLines || null)}
                        discardedLines={salesResult?.discards?.total}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default DataUploadPage;
